import ErrorSummary from '@root/core/src/components/error-summary';
import Input, { TextTypes } from '@root/core/src/components/input';
import LoaderButton from '@root/core/src/components/loader-button';
import PropTypes from '@root/vendor/prop-types';
import React, { useState } from '@root/vendor/react';
import useForm from '@root/core/src/hooks/use-form';
import { EmailConfirmationModal } from '@root/core/src/components/modals/email-confirmation-modal';
import { StyleSheet } from '@root/core/src/utils/styles';
import { isRequired, isValidEmail } from '@root/core/src/utils/validators';
import { loginEmail } from '@root/login/src/services/login-service';
import { shouldConfirmEmail } from '@root/core/src/utils/common-email-validator';
import { useRootId } from '@root/attribution/src/hooks/use-root-id';

const validations = [
  isRequired('email'),
  isValidEmail('email'),
  isRequired('password'),
];

export default function LoginEmailPassword({ onLogin, analytics }) {
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [isError, setIsError] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const rootId = useRootId();

  const form = useForm({
    validations,
  });

  const login = async ({ email, password }) => {
    analytics.trackClick('CONTINUE');

    const loginResult = await loginEmail(email, password, rootId);

    if (loginResult.isSuccess) {
      onLogin && onLogin();
    } else {
      analytics.trackEvent('REQUEST_LOGIN_ERROR');
      setIsError(true);
    }

    setSubmitting(false);
  };

  const errorSummary = isError ? (
    <ErrorSummary
      heading={'Either the email or the password isn’t working.'}
      message={'Please try again.'}
    />
  ) : null;

  const handleSubmission = (values) => {
    if (!emailConfirmed && shouldConfirmEmail(values.email)) {
      return setModalVisible(true);
    }

    setSubmitting(true);
    login(values);
  };

  const handleConfirmEmailPressed = () => {
    setEmailConfirmed(true);
    setModalVisible(false);
    setSubmitting(true);
    login(form.values);
  };

  const handleEditEmailPressed = () => {
    setEmailConfirmed(true);
    setModalVisible(false);
  };

  return (
    <>
      {modalVisible &&
        <EmailConfirmationModal
          email={form.values.email}
          isShowing={modalVisible}
          onCancel={() => setModalVisible(false)}
          onConfirmEmailPress={handleConfirmEmailPressed}
          onEditEmailPress={handleEditEmailPressed}
        />
      }
      <form
        data-testid={'login-form'}
        onSubmit={form.createSubmitHandler(handleSubmission)}
      >
        {errorSummary}
        <div css={styles.firstInputContainer}>
          <Input
            id={'email'}
            label={'Email'}
            {...form.getFieldProps('email')}
            autoCapitalize={'off'}
            inputType={TextTypes.TEXT}
          />
        </div>
        <Input
          id={'password'}
          label={'Password'}
          {...form.getFieldProps('password')}
          inputType={TextTypes.PASSWORD}
        />
        <LoaderButton
          isLoading={form.submitting || submitting}
          loadingText={'Sending...'}
        >
          Continue
        </LoaderButton>
      </form>
    </>
  );
}

LoginEmailPassword.propTypes = {
  analytics: PropTypes.object.isRequired,
  onLogin: PropTypes.func,
};

const styles = StyleSheet.create({
  firstInputContainer: {
    paddingBottom: 10,
  },
});
